// askSmart-ui/src/pages/TOSP/components/SplitMappingFields.tsx
import React from "react";
import {
  PERCENT_SPLIT,
  SplitAmong,
  SplitBasis,
  SplitMapping,
  splitPercentError,
  splitPercentTotal,
  SPLIT_AMONG_OPTIONS,
  SPLIT_BASIS_OPTIONS,
  splitEntityLabel,
} from "../types";
import { parseNumeric } from "../validation";

export interface EntityOption {
  value: string;
  label: string;
}

interface SplitMappingFieldsProps {
  value: SplitMapping;
  onChange: (patch: Partial<SplitMapping>) => void;
  /** What the middle dropdown offers, given what's being split among. */
  entityOptions: (splitAmong: SplitAmong | "") => EntityOption[];
  /** Unique per row, so each select's label points at its own control. */
  idPrefix: string;
}

// The three linked choices that describe how a cost is divided: what it's
// split among, which one of those, and on what basis.
export default function SplitMappingFields({
  value,
  onChange,
  entityOptions,
  idPrefix,
}: SplitMappingFieldsProps) {
  const options = entityOptions(value.splitAmong);
  const entityLabel = splitEntityLabel(value.splitAmong);
  const selected = value.entities || [];
  const allValues = options.map((o) => o.value);
  const percentages = value.percentages || {};
  const showPercentages = value.basedOn === PERCENT_SPLIT && selected.length > 0;
  const percentTotal = splitPercentTotal(value);
  const percentError = splitPercentError(value);

  /** Drops percentages for entities no longer selected. */
  const prunePercentages = (entities: string[]): Record<string, number> =>
    Object.fromEntries(
      Object.entries(percentages).filter(([entity]) => entities.includes(entity)),
    );

  const setEntities = (entities: string[]) =>
    onChange({ entities, percentages: prunePercentages(entities) });

  const toggleEntity = (entity: string, checked: boolean) =>
    setEntities(checked ? [...selected, entity] : selected.filter((v) => v !== entity));

  const setPercentage = (entity: string, raw: string) => {
    const next = { ...percentages };
    const parsed = parseNumeric(raw);
    if (!raw.trim() || parsed === null) delete next[entity];
    else next[entity] = parsed;
    onChange({ percentages: next });
  };

  // Equal shares, with the rounding remainder given to the first entity so the
  // total still lands exactly on 100.
  const distributeEvenly = () => {
    const share = Math.floor((100 / selected.length) * 100) / 100;
    const next: Record<string, number> = {};
    selected.forEach((entity) => {
      next[entity] = share;
    });
    if (selected.length > 0) {
      const remainder = Math.round((100 - share * selected.length) * 100) / 100;
      next[selected[0]] = Math.round((share + remainder) * 100) / 100;
    }
    onChange({ percentages: next });
  };

  return (
    <>
      <div className="tosp-field">
        <label className="tosp-label" htmlFor={`${idPrefix}-among`}>
          Split Among
        </label>
        <select
          id={`${idPrefix}-among`}
          className="manage-input"
          value={value.splitAmong}
          // Changing what it splits among invalidates the entity.
          onChange={(e) =>
            onChange({ splitAmong: e.target.value as SplitAmong, entities: [] })
          }
        >
          <option value="">— Select —</option>
          {SPLIT_AMONG_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* Several at once — a cost is usually divided across a set, e.g. every
          waste band except Exempt. */}
      <div className="tosp-field">
        <span className="tosp-label" id={`${idPrefix}-entity-label`}>
          {entityLabel}
          {selected.length > 0 && (
            <span className="tosp-checklist-count"> · {selected.length} selected</span>
          )}
        </span>

        {!value.splitAmong ? (
          <div className="tosp-calc-value">— Choose Split Among first —</div>
        ) : options.length === 0 ? (
          <div className="tosp-calc-value">Nothing to select</div>
        ) : (
          <div
            className="tosp-checklist"
            role="group"
            aria-labelledby={`${idPrefix}-entity-label`}
          >
            <div className="tosp-checklist-actions">
              <button type="button" onClick={() => setEntities(allValues)}>
                Select all
              </button>
              <button type="button" onClick={() => setEntities([])}>
                Clear
              </button>
            </div>
            {options.map((option) => (
              <label key={option.value} className="tosp-checklist-item">
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={(e) => toggleEntity(option.value, e.target.checked)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="tosp-field">
        <label className="tosp-label" htmlFor={`${idPrefix}-basis`}>
          Based On
        </label>
        <select
          id={`${idPrefix}-basis`}
          className="manage-input"
          value={value.basedOn}
          onChange={(e) => onChange({ basedOn: e.target.value as SplitBasis })}
        >
          <option value="">— Select —</option>
          {SPLIT_BASIS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* A percentage split needs a share against each entity, and the shares
          have to account for the whole cost — so the total is shown as it's
          typed and the form won't save until it reaches 100%. */}
      {showPercentages && (
        <div className="tosp-percent-split">
          <div className="tosp-percent-header">
            <span className="tosp-label" style={{ marginBottom: 0 }}>
              Percentage of each {entityLabel.toLowerCase()}
            </span>
            <button type="button" className="tosp-link-btn" onClick={distributeEvenly}>
              Distribute evenly
            </button>
            <span className={`tosp-percent-total${percentError ? " tosp-percent-off" : ""}`}>
              {Number(percentTotal.toFixed(2))}% of 100%
            </span>
          </div>

          <div className="tosp-percent-list">
            {selected.map((entity) => {
              const label = options.find((o) => o.value === entity)?.label || entity;
              return (
                <div key={entity} className="tosp-percent-row">
                  <span className="tosp-percent-name" title={label}>
                    {label}
                  </span>
                  <div className="tosp-affix-wrap">
                    <input
                      className="manage-input tosp-has-suffix"
                      autoComplete="off"
                      inputMode="decimal"
                      aria-label={`Percentage for ${label}`}
                      value={percentages[entity] ?? ""}
                      onChange={(e) => setPercentage(entity, e.target.value)}
                    />
                    <span className="tosp-affix tosp-affix-suffix">%</span>
                  </div>
                </div>
              );
            })}
          </div>

          {percentError && <p className="tosp-field-error">{percentError}</p>}
        </div>
      )}
    </>
  );
}
